import { useMutation, useQueryClient } from "@tanstack/react-query";
import React, { useEffect, useState } from "react";
import { toast } from "sonner";

import { searchStructuralUnits } from "@/api/one_c_pickers";
import * as api from "@/api/system_settings";
import { RemotePicker, type PickerRow } from "@/components/pickers/RemotePicker";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import type { SystemSettingsView } from "@/types/api";

/** Склады возвратов: куда едет утиль, куда — переупаковка и куда — «на ФБС».
 *
 * ⚠️ Переехали сюда из переменных окружения: это выбор человека, который
 * меняют по мере надобности, а не реквизит развёртывания — держать его в env
 * значило требовать редеплой ради выбора склада.
 *
 * Склад стоит в СТРОКЕ «Возврата от комиссионера» (`ПоложениеСклада =
 * ВТабличнойЧасти`), поэтому смешанная приёмка — один документ на юрлицо; у
 * Яндекса (перемещение) склад в шапке — документ на причину. */
export function ReturnsWarehousesCard({
  data,
}: {
  data: SystemSettingsView;
}): React.ReactElement {
  const qc = useQueryClient();
  const [utility, setUtility] = useState<PickerRow | null>(null);
  const [repack, setRepack] = useState<PickerRow | null>(null);
  const [fbs, setFbs] = useState<PickerRow | null>(null);

  useEffect(() => {
    setUtility(
      data.returns_utility_warehouse_ref
        ? {
            ref: data.returns_utility_warehouse_ref,
            name: data.returns_utility_warehouse_name ?? "",
          }
        : null,
    );
    setRepack(
      data.returns_repack_warehouse_ref
        ? {
            ref: data.returns_repack_warehouse_ref,
            name: data.returns_repack_warehouse_name ?? "",
          }
        : null,
    );
    setFbs(
      data.returns_fbs_warehouse_ref
        ? {
            ref: data.returns_fbs_warehouse_ref,
            name: data.returns_fbs_warehouse_name ?? "",
          }
        : null,
    );
  }, [data]);

  const save = useMutation({
    mutationFn: () =>
      api.updateSystemSettings({
        returns_utility_warehouse_ref: utility?.ref ?? "",
        returns_utility_warehouse_name: utility?.name ?? "",
        returns_repack_warehouse_ref: repack?.ref ?? "",
        returns_repack_warehouse_name: repack?.name ?? "",
        returns_fbs_warehouse_ref: fbs?.ref ?? "",
        returns_fbs_warehouse_name: fbs?.name ?? "",
      }),
    onSuccess: () => {
      toast.success("Склады возвратов сохранены");
      void qc.invalidateQueries({ queryKey: ["system-settings"] });
    },
    onError: () => toast.error("Не удалось сохранить"),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Склады возвратов</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label>Склад утилизации</Label>
          <RemotePicker
            value={utility}
            onChange={setUtility}
            search={(q) => searchStructuralUnits("Склад", q)}
            queryKey={["ret-wh-utility"]}
            placeholder="Склад просроченного товара"
          />
          <p className="text-xs text-muted-foreground">
            Куда едет принятое на утиль. Без него обработка возвратов
            отказывается создавать документы — иначе они молча ушли бы на склад
            из документа-шаблона.
          </p>
        </div>
        <div className="space-y-2">
          <Label>Склад переупаковки</Label>
          <RemotePicker
            value={repack}
            onChange={setRepack}
            search={(q) => searchStructuralUnits("Склад", q)}
            queryKey={["ret-wh-repack"]}
            placeholder="Склад переупаковки"
          />
          <p className="text-xs text-muted-foreground">
            Куда едет принятое на переупаковку: там товар живой и вернётся в
            продажу. Не выбран — едет вместе с утилем, как раньше.
          </p>
        </div>
        <div className="space-y-2">
          <Label>Склад ФБС</Label>
          <RemotePicker
            value={fbs}
            onChange={setFbs}
            search={(q) => searchStructuralUnits("Склад", q)}
            queryKey={["ret-wh-fbs"]}
            placeholder="Склад ФБС"
          />
          <p className="text-xs text-muted-foreground">
            Куда сразу едет принятое «на ФБС» — на то же юрлицо, на которое
            пришёл возврат. Не выбран — обработка приёмки с такими позициями
            отказывает (на склад утиля они не уходят).
          </p>
        </div>
        <Button onClick={() => save.mutate()} disabled={save.isPending}>
          Сохранить
        </Button>
      </CardContent>
    </Card>
  );
}
